import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../axiosConfig";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Lightbox, PhotoCell } from "../components/PhotoViewer";
import "../assets/CSS/Dashboard.css";
import "./WorkerSupportEscalationView.css";

const statusClass = value => String(value || "Open").toLowerCase().replace(/\s+/g, "-");

const formatDate = value => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRemaining = value => {
  if (!value) return "No further escalation";
  const seconds = Math.max(0, Math.floor((new Date(value).getTime() - Date.now()) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return seconds <= 0 ? "Escalation due now" : `${hours}h ${minutes}m remaining`;
};

function WorkerSupportEscalationView() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [payload, setPayload] = useState({ viewer: null, levels: [], tickets: [], mappingWarnings: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Active");
  const [level, setLevel] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [, setClockTick] = useState(0);

  const fetchEscalations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get("/api/worker-support-escalations");
      setPayload(response.data);
    } catch (requestError) {
      const message = requestError.response?.data?.message || "Escalation tickets could not be loaded.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEscalations(); }, [fetchEscalations]);
  useEffect(() => {
    const timer = window.setInterval(() => setClockTick(value => value + 1), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const levelDetails = useCallback(levelNumber => (
    payload.levels.find(item => Number(item.levelNumber) === Number(levelNumber))
  ), [payload.levels]);

  const ownerForTicket = useCallback(ticket => {
    if (Number(ticket.EscalationLevel) === 0) {
      return { label: "Assigned supervisor", names: ticket.Supervisor_Name || "Supervisor" };
    }
    const stage = levelDetails(ticket.EscalationLevel);
    return {
      label: `Escalation level ${ticket.EscalationLevel}`,
      names: stage?.recipients.map(recipient => recipient.name).join(" + ") || "Escalation recipients",
    };
  }, [levelDetails]);

  const nextForTicket = useCallback(ticket => {
    const nextLevel = Number(ticket.EscalationLevel) + 1;
    const stage = levelDetails(nextLevel);
    if (!stage) return null;
    return {
      level: nextLevel,
      names: stage.recipients.map(recipient => recipient.name).join(" + "),
    };
  }, [levelDetails]);

  const visibleTickets = useMemo(() => payload.tickets.filter(ticket => {
    const haystack = [
      ticket.TicketID,
      ticket.Worker_Name,
      ticket.Supervisor_Name,
      ticket.Department,
      ticket.Subject,
      ticket.TicketDescription,
    ].join(" ").toLowerCase();
    const ticketStatus = String(ticket.TicketStatus || "Open");
    const matchesSearch = !search || haystack.includes(search.toLowerCase());
    const matchesStatus = status === "" ||
      (status === "Active" && !["Solved", "Closed"].includes(ticketStatus)) ||
      ticketStatus === status;
    const matchesLevel = level === "" || Number(ticket.EscalationLevel) === Number(level);
    return matchesSearch && matchesStatus && matchesLevel;
  }), [payload.tickets, search, status, level]);

  const counts = useMemo(() => ({
    active: payload.tickets.filter(ticket => !["Solved", "Closed"].includes(ticket.TicketStatus)).length,
    due: payload.tickets.filter(ticket => ticket.NextEscalationAt && new Date(ticket.NextEscalationAt) <= new Date()).length,
    escalated: payload.tickets.filter(ticket => Number(ticket.EscalationLevel) > 0 && ticket.TicketStatus !== "Solved").length,
    solved: payload.tickets.filter(ticket => ticket.TicketStatus === "Solved").length,
  }), [payload.tickets]);

  const updateStatus = async (ticketId, TicketStatus) => {
    setUpdatingId(ticketId);
    setNotice("");
    try {
      const response = await axiosInstance.patch(`/api/worker-support-escalations/${ticketId}/status`, { TicketStatus });
      setNotice(response.data?.message || `Ticket #${ticketId} updated.`);
      await fetchEscalations();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Ticket status could not be updated.");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="d-flex dashboard wse-shell">
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}><Sidebar /></div>
      <div className="flex-grow-1 wse-page">
        <Header toggleSidebar={() => setIsSidebarVisible(value => !value)} isSidebarVisible={isSidebarVisible} />
        <main className="main-container wse-content">
          <section className="wse-hero">
            <div>
              <span className="wse-eyebrow">WORKER SUPPORT · 12-HOUR ESCALATION</span>
              <h1>Worker Support Escalations</h1>
              <p>Workers ke unresolved problem/support tickets. Har 12 hours me responsibility next escalation team ko automatically jaati hai.</p>
            </div>
            {payload.viewer && (
              <div className="wse-viewer">
                <span>YOU ARE VIEWING AS</span>
                <strong>{payload.viewer.name}</strong>
                <small>{payload.viewer.auth} · ID {payload.viewer.employeeId}</small>
                <em>{payload.viewer.visibility}</em>
              </div>
            )}
          </section>

          {error && <div className="wse-alert error"><i className="bi bi-exclamation-triangle-fill" />{error}<button type="button" onClick={fetchEscalations}>Retry</button></div>}
          {notice && <div className="wse-alert success"><i className="bi bi-check-circle-fill" />{notice}</div>}

          {!error && (
            <>
              <section className="wse-path" aria-label="Escalation path">
                <div className="wse-path-step initial"><span>START</span><strong>Assigned Supervisor</strong><small>First 12 hours</small></div>
                {payload.levels.map(stage => (
                  <React.Fragment key={stage.levelNumber}>
                    <i className="bi bi-arrow-right wse-path-arrow" />
                    <div className={`wse-path-step level-${stage.levelNumber}`}>
                      <span>LEVEL {stage.levelNumber} · AFTER {stage.delayHours * stage.levelNumber}H</span>
                      <strong>{stage.recipients.map(recipient => recipient.name).join(" + ")}</strong>
                      <small>
                        {stage.recipients.some(recipient => recipient.mappingPending)
                          ? "Employee mapping pending"
                          : stage.recipients.map(recipient => `ID ${recipient.employeeId}`).join(" · ")}
                      </small>
                    </div>
                  </React.Fragment>
                ))}
              </section>

              {payload.mappingWarnings.length > 0 && (
                <div className="wse-alert warning">
                  <i className="bi bi-person-exclamation" />
                  <div><strong>Recipient mapping required:</strong> {payload.mappingWarnings.join(", ")}. Mapping hone tak SuperAdmin in tickets ko monitor kar sakta hai.</div>
                </div>
              )}

              <section className="wse-stats">
                <div><i className="bi bi-lightning-charge-fill" /><span>Active tickets<strong>{counts.active}</strong></span></div>
                <div><i className="bi bi-arrow-up-right-circle-fill" /><span>Escalated<strong>{counts.escalated}</strong></span></div>
                <div><i className="bi bi-alarm-fill" /><span>Due now<strong>{counts.due}</strong></span></div>
                <div><i className="bi bi-check-circle-fill" /><span>Solved<strong>{counts.solved}</strong></span></div>
              </section>

              <section className="wse-board">
                <div className="wse-board-head">
                  <div><h2>Tickets requiring attention</h2><p>Current owner, next escalation aur worker ki complete problem details.</p></div>
                  <span>{visibleTickets.length} of {payload.tickets.length} shown</span>
                </div>
                <div className="wse-filters">
                  <label><i className="bi bi-search" /><input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search ticket, worker, supervisor or issue..." /></label>
                  <select value={status} onChange={event => setStatus(event.target.value)} aria-label="Filter by status">
                    <option value="">All statuses</option>
                    <option value="Active">Active only</option>
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Solved">Solved</option>
                  </select>
                  <select value={level} onChange={event => setLevel(event.target.value)} aria-label="Filter by escalation level">
                    <option value="">All levels</option>
                    <option value="0">Supervisor stage</option>
                    <option value="1">Level 1</option>
                    <option value="2">Level 2</option>
                    <option value="3">Level 3</option>
                  </select>
                  <button type="button" onClick={fetchEscalations}><i className="bi bi-arrow-clockwise" />Reload</button>
                </div>

                {loading ? (
                  <div className="wse-empty"><div className="spinner-border text-primary" role="status" /><strong>Loading escalation tickets…</strong></div>
                ) : visibleTickets.length === 0 ? (
                  <div className="wse-empty"><i className="bi bi-inbox" /><strong>No tickets match these filters.</strong><span>Filters clear karke dobara check karein.</span></div>
                ) : (
                  <div className="wse-ticket-list">
                    {visibleTickets.map(ticket => {
                      const owner = ownerForTicket(ticket);
                      const next = nextForTicket(ticket);
                      const solved = ticket.TicketStatus === "Solved";
                      return (
                        <article className={`wse-ticket ${statusClass(ticket.TicketStatus)}`} key={ticket.TicketID}>
                          <div className="wse-ticket-top">
                            <div className="wse-ticket-number"><span>TICKET</span><strong>#{ticket.TicketID}</strong></div>
                            <div className="wse-worker"><span>RAISED BY WORKER</span><strong>{ticket.Worker_Name}</strong><small>{ticket.Department}</small></div>
                            <div className="wse-owner"><span>{solved ? "RESOLVED" : "CURRENT RESPONSIBILITY"}</span><strong>{solved ? (ticket.SolvedBy || "Resolved") : owner.names}</strong><small>{solved ? formatDate(ticket.TicketSolve_date) : owner.label}</small></div>
                            <div className={`wse-status ${statusClass(ticket.TicketStatus)}`}>{ticket.TicketStatus || "Open"}</div>
                          </div>

                          <div className="wse-ticket-body">
                            <div className="wse-issue">
                              <span>WORKER'S ISSUE</span>
                              <h3>{ticket.Subject || "No subject"}</h3>
                              <p>{ticket.TicketDescription || "No additional details provided."}</p>
                              <div className="wse-meta"><span><i className="bi bi-person-badge" />Against: <strong>{ticket.Supervisor_Name}</strong> (ID {ticket.SupervisorID})</span><span><i className="bi bi-calendar3" />Raised: <strong>{formatDate(ticket.CreatedDate)}</strong></span></div>
                            </div>
                            <div className="wse-photo"><span>ATTACHMENT</span><PhotoCell src={ticket.photo_path} label="Ticket photo" onView={setLightbox} /></div>
                          </div>

                          <div className="wse-ticket-foot">
                            <div className="wse-progress">
                              <div className="wse-progress-label">
                                <strong>{solved ? "Escalation stopped — ticket solved" : next ? `Next: Level ${next.level} · ${next.names}` : "Final escalation level reached"}</strong>
                                <span>{solved ? "No further action" : formatRemaining(ticket.NextEscalationAt)}</span>
                              </div>
                              <div className="wse-mini-path">
                                {[0, 1, 2, 3].map(stage => <span key={stage} className={Number(ticket.EscalationLevel) >= stage ? "reached" : ""}>{stage === 0 ? "Supervisor" : `L${stage}`}</span>)}
                              </div>
                            </div>
                            <label className="wse-action">
                              <span>UPDATE STATUS</span>
                              <select disabled={updatingId === ticket.TicketID} value={ticket.TicketStatus} onChange={event => updateStatus(ticket.TicketID, event.target.value)}>
                                <option value="Open">Open — action needed</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Solved">Solved</option>
                              </select>
                            </label>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default WorkerSupportEscalationView;

